import { type ActivatedRouteSnapshot, ActivationEnd, type Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { EMPTY, filter, first, map, merge, of } from 'rxjs';

/**
 * safe test if route is already activated
 * usefull for race concurency with router events :
 * - ActivationStart
 *   - ✅ Has event.snapshot.routeConfig
 *   - ❌ Router state still old
 * - ResolveEnd
 *   - ❌ NO event.snapshot at all
 *   - ✅ Router state updated with resolved data
 * - ActivationEnd
 *   - ✅ Has event.snapshot.routeConfig
 *   - ✅ Router state fully updated
 *
 * @param router must provide router since injected in angular environment
 * @returns only if route is already activated
 */
export function activatedRoute$(
  router?: Router | null | undefined,
): Observable<ActivatedRouteSnapshot> {
  if (!router) return EMPTY;

  const currentResolvedRoute$ = of(router.routerState.snapshot.root).pipe(
    filter(Boolean),
    map((route) => {
      while (route.children?.length) {
        route = route.children[0];
      }

      return route;
    }),
    filter(({ routeConfig }) => !!routeConfig),
  );

  const onActivationEnd$ = router.events.pipe(
    filter((event) => event instanceof ActivationEnd),
    map(({ snapshot }) => snapshot),
  );

  return merge(currentResolvedRoute$, onActivationEnd$).pipe(first());
}
